import { doc, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/config";
import { userService } from "./userService";
import { notificationService } from "./notificationService";
import { StripeService } from "./stripeService";
import {
  addWellnessLogAndLinkToDayPost,
  calculateAndSavePeakScore,
} from "./dayPostService";
import type { UserData } from "../types/user";
import type { WellnessLog } from "../types/post";
import type { OnboardingWellnessDraft } from "../hooks/useOnboardingWizard";
import { FINISH_STATUS } from "../components/onboarding/onboardingCopy";
import { todayDateID, wellnessScore } from "../components/onboarding/onboardingValidation";
import { safeGetItem } from "../utils/localStorageUtils";

/** Everything the finish step needs from the wizard state. */
export interface OnboardingRegistrationInput {
  uid: string;
  /** Auth email when available, else the typed one. */
  email: string;
  firstName: string;
  username: string;
  dob: string;
  sex: "female" | "male" | null;
  selectedSportIDs: string[];
  successLook: string;
  competitionLevel: string | null;
  inviteCode: string;
  /** Merge into an existing users/{uid} instead of creating it. */
  existingProfile?: boolean;
}

/**
 * Mirrors the `UserData` payload in iOS `OnboardingController.finish`:
 * role Athlete, onboardingComplete true, the free-text "what does success
 * look like?" answer stored as the athlete's goals text (`performanceGoals`,
 * Events.md §2), every other field default.
 *
 * isEmailVerified is not included. The wizard draft can claim a provider
 * vouched for the address, and the owner can write private/data, so the
 * client must not assert that flag. verifyEmailCode sets it server-side.
 */
export function buildRegistrationPayload(
  input: OnboardingRegistrationInput,
): UserData {
  const goal = input.successLook.trim();
  const campaignCode = safeGetItem("campaignCode")?.trim() ?? "";

  return {
    // private/data
    email: input.email,
    name: input.firstName.trim(),
    surname: "",
    dob: input.dob,
    gender: input.sex ?? "",
    country: "",
    // users/{uid}
    username: input.username.trim(),
    role: "Athlete",
    isCoach: false,
    trainingSportIDs: [...input.selectedSportIDs],
    cycleDays: 7,
    blockDays: 28,
    isImperial: false,
    onboardingComplete: true,
    reason: "",
    simpleWellnessSetting: false,
    isPrivateProfile: false,
    deActivated: false,
    sleepGoal: 8,
    loadGoal: 400,
    weeklyLoadGoal: 2800,
    performanceGoals: goal,
    healthGoals: "",
    casualMode: false,
    disableInsights: false,
    autoPostTraining: false,
    hidePeakScore: false,
    hideWellnessSleep: false,
    ...(campaignCode && { campaignCode }),
  };
}

/**
 * Links the signed-in athlete to a coach organisation. The code is the one
 * from `assignInviteCode`; the callable rejects unknown, used, and expired codes.
 */
export async function redeemInviteCode(code: string): Promise<void> {
  const redeemFn = httpsCallable(functions, "redeemInviteCode");
  await redeemFn({ code: code.trim() });
}

/**
 * Narrow update for a profile that already exists. A skipped goal is left
 * out so an empty string cannot replace goals the account already stored.
 */
export function buildExistingProfileUpdate(payload: UserData): Partial<UserData> {
  const update: Partial<UserData> = { onboardingComplete: true };
  if (payload.trainingSportIDs && payload.trainingSportIDs.length > 0) {
    update.trainingSportIDs = payload.trainingSportIDs;
  }
  if (payload.performanceGoals) {
    update.performanceGoals = payload.performanceGoals;
  }
  if (payload.name) update.name = payload.name;
  if (payload.username) update.username = payload.username;
  if (payload.gender) update.gender = payload.gender;
  if (payload.dob) update.dob = payload.dob;
  return update;
}

/** Fields merged onto users/{uid} right after the profile write (iOS "extras"). */
export function buildExtrasPayload(input: OnboardingRegistrationInput): Partial<UserData> {
  const extras: Partial<UserData> = {};
  if (input.competitionLevel) extras.competitionLevel = input.competitionLevel;
  const invite = input.inviteCode.trim();
  if (invite) extras.inviteCode = invite;
  return extras;
}

export function buildWellnessLog(
  wellness: OnboardingWellnessDraft,
): Omit<WellnessLog, "timestamp" | "dateID"> {
  const log: Omit<WellnessLog, "timestamp" | "dateID"> = {
    health: wellness.health,
    mood: wellness.mood,
    physical: wellness.physical,
    recovery: wellness.recovery,
    sleep: wellness.sleep,
    stress: wellness.stress,
    sleepTime: wellness.sleepTime,
    sick: wellness.sick,
    soreness: wellness.soreness,
    injured: wellness.injured,
    wellnessScore: wellnessScore(wellness),
  };
  if (wellness.injured) log.trainingCapacity = wellness.trainingCapacity;
  return log;
}

/**
 * iOS `logFirstWellness`: wellnessLogs/{uid}/logs + dayPosts/{uid}/posts/{dateID}
 * link, then a fire-and-forget `calculateAndSavePeakScore`. Runs before the
 * profile doc exists (firestore.rules only require auth.uid == userId).
 */
export async function logFirstWellness(
  uid: string,
  wellness: OnboardingWellnessDraft,
  dateID: string = todayDateID(),
): Promise<string> {
  const { wellnessLogId } = await addWellnessLogAndLinkToDayPost(uid, buildWellnessLog(wellness), dateID);
  calculateAndSavePeakScore(dateID).catch(() => {});
  return wellnessLogId;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface CompleteOnboardingOptions {
  /** Local hour 0–23, or null when the user skipped the reminder. */
  reminderHour: number | null;
  onStatus?: (status: string) => void;
  /** Test seam — skip the cosmetic pauses. */
  pauses?: boolean;
}

/**
 * Profile write, extras merge, then the optional daily reminder.
 * The welcome email is not sent here. Onboarding is finished when the user
 * reaches the download page, and that page sends it.
 * Only the profile write is fatal; everything after it is best-effort.
 */
export async function completeOnboarding(
  input: OnboardingRegistrationInput,
  options: CompleteOnboardingOptions,
): Promise<void> {
  const { reminderHour, onStatus, pauses = true } = options;
  const status = (s: string) => onStatus?.(s);

  status(FINISH_STATUS.profile);
  const payload = buildRegistrationPayload(input);
  // The wizard's existingProfile flag is localStorage state and can be stale.
  // Read the server doc before deciding to create. A failed read must not
  // fall through to a full replace.
  let serverProfile: UserData | null;
  try {
    serverProfile = await userService.getUserData(input.uid);
  } catch (error) {
    console.error("[Onboarding] could not read the existing profile:", error);
    throw new Error("Could not load your profile. Please try again.");
  }

  const alreadyComplete = serverProfile?.onboardingComplete === true;
  if (!alreadyComplete) {
    if (serverProfile || input.existingProfile) {
      await userService.updateUserProfile(input.uid, buildExistingProfileUpdate(payload));
    } else {
      await userService.completeUserRegistration(input.uid, payload);
    }
  }

  status(FINISH_STATUS.extras);
  const extras = buildExtrasPayload(input);
  if (!alreadyComplete && Object.keys(extras).length > 0) {
    try {
      await userService.updateUserProfile(input.uid, extras);
    } catch (error) {
      console.warn("[Onboarding] extras merge failed (non-fatal):", error);
    }
  }
  if (!alreadyComplete && payload.campaignCode) userService.cleanupCampaignCode(payload.campaignCode);
  if (pauses) await sleep(450);

  if (reminderHour !== null) {
    status(FINISH_STATUS.reminder);
    try {
      // FCM token + reminders/{gmtHour}/users/{uid} + users/{uid}.reminderGmtHour
      await notificationService.saveDailyReminder(input.uid, reminderHour);
    } catch (error) {
      console.warn("[Onboarding] daily reminder not saved (non-fatal):", error);
    }
    try {
      await setDoc(
        doc(db, "users", input.uid),
        { notifySettings: { pushNotification: { logReminder: true } } },
        { merge: true },
      );
    } catch (error) {
      console.warn("[Onboarding] notifySettings merge failed (non-fatal):", error);
    }
  }

  status(FINISH_STATUS.dashboard);
  if (pauses) await sleep(600);
}

/** Welcome email for a user who has reached the download page. The function sends once. */
export async function sendWelcomeEmail(email: string, firstName: string): Promise<void> {
  const processNewUser = httpsCallable(functions, "processNewUserV2");
  await processNewUser({ email, firstName });
}

export interface PaidPlanSelection {
  type: "plus" | "premium";
  name: string;
  billingPeriod: "month" | "quarter" | "year";
}

/**
 * The paid onboarding path: flat USD pricing through the new (PEAK) Stripe
 * account — no region lookup, no pre-created customer (the checkout endpoint
 * creates one lazily for this account). 14-day trial unless `hasUsedTrial`.
 */
export async function createPlanCheckoutUrl(params: {
  uid: string;
  email: string;
  plan: PaidPlanSelection;
  validatedCountry: string;
  stripeCustomerId?: string | null;
  hasUsedTrial?: boolean;
  origin: string;
}): Promise<string | null> {
  const { uid, plan, hasUsedTrial, origin } = params;

  const priceId = StripeService.getPeakPriceId(
    plan.type,
    plan.billingPeriod === "year" ? "year" : "month",
  );

  const successUrl = `${origin}/download?subscription_success=true`;
  const cancelUrl = `${origin}/download?subscription_canceled=true`;
  const trialDays = hasUsedTrial ? 0 : 14;

  return StripeService.createCheckoutSession(
    priceId,
    "",
    successUrl,
    cancelUrl,
    uid,
    plan.name,
    trialDays,
  );
}
